import React from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { FaCalendarAlt, FaMapMarkerAlt, FaExternalLinkAlt } from 'react-icons/fa';
import OptimizedImage from '../components/utils/OptimizedImage';
import '../styles/event-card.css';

const EventsPage = () => {
  // Event data
  const events = [
    {
      id: 1,
      title: 'Online All Island Robotics Challenge 2024',
      date: 'June 15 Saturday, 2024',
      location: 'STEM Labs - Delkanda',
      description: 'Specially designed to gather new Innovators to the STEM Labs with new innovative ideas. Students will showcase their robotic creations and compete in various challenges.',
      image: '/images/coming_events/rc_c.jpg',
      registrationLink: 'https://docs.google.com/forms/d/e/1FAIpQLSfP6lqznlJRU_mQCkkUrxNPNMNRVuJThBLgX93pKc3TXjawwQ/viewform'
    },
    {
      id: 2,
      title: 'STEMTronics Electronics Workshop',
      date: 'July 10 Wednesday, 2024',
      location: 'STEM Labs - Delkanda',
      description: 'Specially designed for covering electronics part in science of school syllabus. Students will learn about electronic components and build circuits.',
      image: '/images/coming_events/stemworkshop.jpg',
      registrationLink: 'https://docs.google.com/forms/d/e/1FAIpQLSeXmT0PRTwJDwA_LsqLzzG1Y_HtTbnJvXS2h_byP7hkBB4UcQ/viewform'
    },
    {
      id: 3,
      title: 'STEM Workshop at Schools',
      date: 'August 5-10, 2024',
      location: 'Government Schools - Colombo',
      description: 'To inspire innovation through school children. This workshop introduces STEM concepts through hands-on activities and demonstrations.',
      image: '/images/coming_events/scl.jpg',
      registrationLink: 'https://docs.google.com/forms/d/e/1FAIpQLSd0J5LDIzZnVGvzSi20-FFTQpkeRn-DL9qEO-Cx2TaMNnnK6g/viewform'
    },
    {
      id: 4,
      title: 'Fighting Robot Competition',
      date: 'September 25, 2024',
      location: 'STEM Labs - Delkanda',
      description: 'This Robot competition designed to develop the students programming skills. Build your own robot and compete against others!',
      image: '/images/coming_events/rbot.jpg',
      registrationLink: 'https://docs.google.com/forms/d/e/1FAIpQLScYwiG39kxW2fS-Cem154KUmeW44Vk0P9DK7ePdU3EkmxTWvA/viewform'
    },
    {
      id: 5,
      title: 'After O/L Robotics [Advanced] Course',
      date: 'October 10, 2024',
      location: 'STEM Labs - Delkanda',
      description: 'This course specially designed to develop innovation skills. 4 months certificate course for students who have completed their O/Ls.',
      image: '/images/coming_events/ol.jpg',
      registrationLink: 'https://docs.google.com/forms/d/e/1FAIpQLSfFoeyVlgf4pyU4aeF42Kxs1vgLq57Lst644KhkwNKvHC0UXA/viewform'
    },
    {
      id: 6,
      title: '3D Design & Printing Workshop',
      date: 'November 15, 2024',
      location: 'STEM Labs - Delkanda',
      description: 'In this session, students can get more knowledge about 3D design & Printing. Learn to create and print your own 3D models.',
      image: '/images/coming_events/3d.jpg',
      registrationLink: 'https://docs.google.com/forms/d/e/1FAIpQLSfO9d_kutN4JmUzDokFCTUjsiBCBrVrj16oe_0-hwVsO80pNQ/viewform'
    }
  ];

  return (
    <>
      <Helmet>
        <title>Upcoming Events | STEM Labs</title>
        <meta name="description" content="Stay updated with the latest events, workshops, and competitions at STEM Labs" />
      </Helmet>
      
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-blue-800 to-blue-600 py-12">
        <div className="container mx-auto px-4">
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-3 text-center">
            Upcoming Events
          </h1>
          <p className="text-white text-base max-w-2xl mx-auto text-center">
            Join us for exciting workshops, competitions, and learning experiences
          </p>
        </div>
      </div>
      
      {/* Events Listing */}
      <section className="py-10">
        <div className="container mx-auto px-4">
          <div className="mb-6 text-center">
            <p className="text-gray-600 max-w-3xl mx-auto text-sm">
              STEM Labs regularly organizes events to inspire innovation and showcase student achievements. 
              Register for our upcoming events to participate in these enriching experiences.
            </p>
          </div>
          
          <div className="events-grid">
            {events.map((event, index) => (
              <motion.div 
                key={event.id}
                className="event-card"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="event-card__image-container">
                  <OptimizedImage 
                    src={event.image} 
                    alt={event.title}
                    className="event-card__image w-full h-full"
                    objectFit="cover"
                    fallbackSrc="https://via.placeholder.com/400x200?text=STEM+Labs+Event"
                  />
                </div>
                <div className="event-card__content">
                  <h2 className="event-card__title">{event.title}</h2>
                  
                  <div className="event-card__meta">
                    <div className="event-card__meta-item">
                      <FaCalendarAlt className="event-card__meta-icon" size={14} />
                      <span>{event.date}</span>
                    </div>
                    <div className="event-card__meta-item">
                      <FaMapMarkerAlt className="event-card__meta-icon" size={14} />
                      <span>{event.location}</span>
                    </div>
                  </div>
                  
                  <p className="event-card__description">{event.description}</p>
                  
                  <a 
                    href={event.registrationLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="event-card__button"
                  >
                    Register now <FaExternalLinkAlt className="event-card__button-icon" size={12} />
                  </a>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      
      {/* CTA Section */}
      <section className="py-10 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-2xl font-bold mb-3">Want to Host a STEM Event?</h2>
            <p className="text-gray-600 mb-6 text-sm">
              STEM Labs can organize custom workshops and events for schools, organizations, and communities. 
              Get in touch with us to discuss how we can bring STEM education to your doorstep.
            </p>
            <a 
              href="https://docs.google.com/forms/d/e/1FAIpQLSfR7AKakDahctDOzwRNX6yHbGfTRzjo9Q669a9EpKkQPsE4VQ/viewform"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-blue-600 text-white py-2 px-6 rounded-full hover:bg-blue-700 transition-colors inline-block text-sm font-medium"
            >
              Request an Event
            </a>
          </div>
        </div>
      </section>
    </>
  );
};

export default EventsPage; 